import { useMemo, useState } from 'react';
import { GripVertical, Plus } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Task, TaskSection } from '../../hooks/queries/useTasks';
import {
  useMoveTask,
  useCreateSection,
  useReorderSections,
} from '../../hooks/queries/useTasks';
import {
  useTasksAssignments,
  useCommentCounts,
  useAttachmentCounts,
} from '../../hooks/queries/useTaskCollaboration';
import type { CollaboratorSummary } from '../../services/tasks/taskCollaborationService';
import { useTaskTagsFor } from '../../hooks/queries/useTaskTags';
import type { TaskTag } from '../../services/tasks/taskTagService';
import { useEffectiveTaskFields } from '../../hooks/useEffectiveTaskFields';
import { useTasksStore } from '../../store/tasksStore';
import { TaskCard } from './TaskCard';
import { TaskComposer } from './TaskComposer';
import { buildSectionGroups } from './taskHelpers';

interface TasksBoardViewProps {
  tasks: Task[];
  sections: TaskSection[];
  dossierId: string | null;
  onOpenTask?: (task: Task) => void;
  hideComposer?: boolean;
}

type ActiveDrag =
  | { kind: 'task'; task: Task }
  | { kind: 'section'; section: TaskSection }
  | null;

export function TasksBoardView({
  tasks,
  sections,
  dossierId,
  onOpenTask,
  hideComposer = false,
}: TasksBoardViewProps) {
  const [active, setActive] = useState<ActiveDrag>(null);
  const moveTask = useMoveTask();
  const createSection = useCreateSection();
  const reorderSections = useReorderSections();
  const [adding, setAdding] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const { sections: sectionsEnabled } = useEffectiveTaskFields();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const pendingDeleteIds = useTasksStore((s) => s.pendingDeleteIds);
  const selectedTaskIds = useTasksStore((s) => s.selectedTaskIds);
  const toggleSelected = useTasksStore((s) => s.toggleSelected);
  const visibleTasks = useMemo(
    () => tasks.filter((t) => !pendingDeleteIds.has(t.id)),
    [tasks, pendingDeleteIds],
  );

  const groups = buildSectionGroups(visibleTasks, sectionsEnabled ? sections : []);
  // Pour le board, ordre = ordre des sections + groupe orphans en tête si non vide
  const orphansGroup = groups.find((g) => g.section === null && g.tasks.length > 0);
  const sectionGroups = sections
    .map((s) => groups.find((g) => g.section?.id === s.id))
    .filter((g): g is { section: TaskSection; tasks: Task[] } => !!g && g.section !== null);

  const taskIds = useMemo(() => visibleTasks.map((t) => t.id), [visibleTasks]);
  const { data: assignmentsMap } = useTasksAssignments(taskIds);
  const { data: commentCounts } = useCommentCounts(taskIds);
  const { data: attachmentCounts } = useAttachmentCounts(taskIds);
  const { data: tagMap = {} } = useTaskTagsFor(taskIds);

  function handleDragStart(e: DragStartEvent) {
    const id = String(e.active.id);
    if (id.startsWith('col:')) {
      const sectionId = id.slice('col:'.length);
      const section = sections.find((s) => s.id === sectionId);
      if (section) setActive({ kind: 'section', section });
      return;
    }
    // sinon : c'est une tâche (id direct = task.id)
    const task = visibleTasks.find((t) => t.id === id);
    if (task) setActive({ kind: 'task', task });
  }

  function handleDragEnd(e: DragEndEvent) {
    const activeId = String(e.active.id);
    const overId = e.over?.id ? String(e.over.id) : null;
    setActive(null);
    if (!overId) return;

    // ── Drag de colonne ──────────────────────────────────────────
    if (activeId.startsWith('col:') && overId.startsWith('col:')) {
      if (!dossierId) return;
      const fromId = activeId.slice('col:'.length);
      const toId = overId.slice('col:'.length);
      if (fromId === toId) return;
      const oldIdx = sections.findIndex((s) => s.id === fromId);
      const newIdx = sections.findIndex((s) => s.id === toId);
      if (oldIdx < 0 || newIdx < 0) return;
      const reordered = arrayMove(sections, oldIdx, newIdx).map((s) => s.id);
      reorderSections.mutate({ dossierId, sectionIds: reordered });
      return;
    }

    // ── Drag de tâche ────────────────────────────────────────────
    const task = visibleTasks.find((t) => t.id === activeId);
    if (!task) return;

    // Drop sur une colonne (vide ou en queue)
    if (overId.startsWith('section:')) {
      const sectionId = overId.slice('section:'.length);
      const targetSectionId = sectionId === '__none' ? null : sectionId;
      if (task.section_id === targetSectionId) {
        const sectionTasks = visibleTasks
          .filter((t) => (t.section_id ?? null) === targetSectionId && !t.parent_id)
          .sort((a, b) => a.task_order - b.task_order);
        if (sectionTasks[sectionTasks.length - 1]?.id === task.id) return;
      }
      moveTask.mutate({ taskId: activeId, targetSectionId, beforeTaskId: null });
      return;
    }

    // Drop juste avant une autre tâche
    if (overId.startsWith('task:')) {
      const beforeTaskId = overId.slice('task:'.length);
      if (beforeTaskId === activeId) return;
      const beforeTask = visibleTasks.find((t) => t.id === beforeTaskId);
      if (!beforeTask) return;
      moveTask.mutate({
        taskId: activeId,
        targetSectionId: beforeTask.section_id,
        beforeTaskId,
      });
    }
  }

  function handleCreateSection() {
    const name = newSectionName.trim();
    if (!name || !dossierId) return;
    createSection.mutate(
      { name, dossier_id: dossierId, color: '#718063' },
      {
        onSuccess: () => {
          setNewSectionName('');
          setAdding(false);
        },
      },
    );
  }

  const sortableIds = sectionGroups.map((g) => `col:${g.section.id}`);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2 items-start">
        {/* Colonne orphans (non sortable, toujours en tête) */}
        {orphansGroup && (
          <BoardColumn
            section={null}
            tasks={orphansGroup.tasks}
            dossierId={dossierId}
            onOpenTask={onOpenTask}
            assignmentsMap={assignmentsMap}
            commentCounts={commentCounts}
            attachmentCounts={attachmentCounts}
            tagMap={tagMap}
            selectedTaskIds={selectedTaskIds}
            onToggleSelect={toggleSelected}
            hideComposer={hideComposer}
          />
        )}

        {/* Colonnes sortables */}
        <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
          {sectionGroups.map((group) => (
            <SortableBoardColumn
              key={group.section.id}
              section={group.section}
              tasks={group.tasks}
              dossierId={dossierId}
              onOpenTask={onOpenTask}
              assignmentsMap={assignmentsMap}
              commentCounts={commentCounts}
              attachmentCounts={attachmentCounts}
              tagMap={tagMap}
              selectedTaskIds={selectedTaskIds}
              onToggleSelect={toggleSelected}
              hideComposer={hideComposer}
            />
          ))}
        </SortableContext>

        {/* Bouton ajouter une colonne */}
        {sectionsEnabled && dossierId && (
          <div className="flex-shrink-0 w-80">
            {adding ? (
              <div className="bg-white rounded-xl border border-cime-border p-3 space-y-2">
                <input
                  autoFocus
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateSection();
                    if (e.key === 'Escape') setAdding(false);
                  }}
                  placeholder="Nom de la colonne"
                  className="w-full text-sm border border-cime-border rounded-md px-2 py-1.5 outline-none focus:border-cime-primary"
                />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleCreateSection}
                    disabled={!newSectionName.trim() || createSection.isPending}
                    className="px-2.5 py-1 rounded text-xs font-medium bg-cime-primary text-white hover:bg-cime-primary/90 disabled:opacity-50"
                  >
                    Ajouter
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAdding(false);
                      setNewSectionName('');
                    }}
                    className="px-2.5 py-1 rounded text-xs text-cime-text-muted hover:bg-[#fbfaf7]"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-cime-border text-sm text-cime-text-muted hover:border-cime-primary hover:text-cime-primary transition"
              >
                <Plus className="w-4 h-4" />
                Ajouter une colonne
              </button>
            )}
          </div>
        )}
      </div>

      <DragOverlay dropAnimation={null}>
        {active?.kind === 'task' && (
          <div className="opacity-90 rotate-1">
            <TaskCard task={active.task} variant="board" />
          </div>
        )}
        {active?.kind === 'section' && (
          <div className="opacity-90 w-80 bg-white rounded-xl border-2 border-cime-primary/40 px-3 py-2.5 shadow-lg">
            <div className="flex items-center gap-2">
              <span
                className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
                style={{ backgroundColor: active.section.color ?? '#718063' }}
                aria-hidden
              />
              <h3 className="font-body text-sm font-semibold text-cime-text-primary">
                {active.section.name}
              </h3>
            </div>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

// ============================================================
// Colonne sortable (header avec poignée drag)
// ============================================================
interface SortableBoardColumnProps {
  section: TaskSection;
  tasks: Task[];
  dossierId: string | null;
  onOpenTask?: (task: Task) => void;
  assignmentsMap?: Map<string, CollaboratorSummary[]>;
  commentCounts?: Map<string, number>;
  attachmentCounts?: Map<string, number>;
  tagMap?: Record<string, TaskTag[]>;
  selectedTaskIds: Set<string>;
  onToggleSelect: (id: string) => void;
  hideComposer?: boolean;
}

function SortableBoardColumn(props: SortableBoardColumnProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: `col:${props.section.id}` });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex-shrink-0">
      <BoardColumn {...props} dragHandleProps={{ attributes, listeners }} />
    </div>
  );
}

// ============================================================
// Colonne (droppable pour les tâches)
// ============================================================
interface BoardColumnProps {
  section: TaskSection | null;
  tasks: Task[];
  dossierId: string | null;
  onOpenTask?: (task: Task) => void;
  assignmentsMap?: Map<string, CollaboratorSummary[]>;
  commentCounts?: Map<string, number>;
  attachmentCounts?: Map<string, number>;
  tagMap?: Record<string, TaskTag[]>;
  selectedTaskIds: Set<string>;
  onToggleSelect: (id: string) => void;
  hideComposer?: boolean;
  dragHandleProps?: {
    attributes: ReturnType<typeof useSortable>['attributes'];
    listeners: ReturnType<typeof useSortable>['listeners'];
  };
}

function BoardColumn({
  section,
  tasks,
  dossierId,
  onOpenTask,
  assignmentsMap,
  commentCounts,
  attachmentCounts,
  tagMap,
  selectedTaskIds,
  onToggleSelect,
  hideComposer = false,
  dragHandleProps,
}: BoardColumnProps) {
  const sectionKey = section?.id ?? '__none';
  const { setNodeRef, isOver } = useDroppable({
    id: `section:${sectionKey}`,
    data: { sectionId: section?.id ?? null },
  });
  const { sections: sectionsEnabled } = useEffectiveTaskFields();
  const title = section?.name ?? (sectionsEnabled ? 'Sans section' : 'Tâches');
  const color = section?.color ?? '#718063';

  return (
    <div
      ref={setNodeRef}
      className={`min-w-100 rounded-xl border flex flex-col max-h-[calc(100vh-260px)] transition ${
        isOver
          ? 'bg-cime-primary/5 border-cime-primary/40'
          : 'bg-[#fbfaf7] border-cime-border'
      }`}
    >
      <header className="flex items-center gap-2 px-3 py-2.5 border-b border-[#efece3]">
        {dragHandleProps && (
          <button
            type="button"
            {...dragHandleProps.attributes}
            {...dragHandleProps.listeners}
            className="text-neutral-300 hover:text-cime-text-muted cursor-grab active:cursor-grabbing -ml-1"
            aria-label="Réorganiser la colonne"
          >
            <GripVertical className="w-4 h-4" />
          </button>
        )}
        <span
          className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
          style={{ backgroundColor: color }}
          aria-hidden
        />
        <h3 className="font-body text-[11px] font-bold! uppercase tracking-[0.08em] text-cime-text-muted truncate">
          {title}
        </h3>
        <span className="text-xs text-cime-text-muted tabular-nums">
          {tasks.length}
        </span>
      </header>

      <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[80px]">
        {tasks.map((t) => (
          <DraggableTaskCard
            key={t.id}
            task={t}
            onOpen={onOpenTask}
            assignees={assignmentsMap?.get(t.id) ?? []}
            commentCount={commentCounts?.get(t.id) ?? 0}
            attachmentCount={attachmentCounts?.get(t.id) ?? 0}
            tags={tagMap?.[t.id]}
            selected={selectedTaskIds.has(t.id)}
            selectionActive={selectedTaskIds.size > 0}
            onToggleSelect={onToggleSelect}
          />
        ))}
        {tasks.length === 0 && (
          <p className="text-xs text-cime-text-muted italic px-2 py-3">Vide</p>
        )}
      </div>

      {section && !hideComposer && (
        <footer className="border-t border-[#efece3] px-3 py-2 bg-white rounded-b-xl">
          <TaskComposer
            dossierId={dossierId}
            sectionId={section.id}
            placeholder="+ Ajouter une tâche"
            compact
          />
        </footer>
      )}
    </div>
  );
}

// ============================================================
// Carte de tâche draggable
// ============================================================
interface DraggableTaskCardProps {
  task: Task;
  onOpen?: (task: Task) => void;
  assignees?: CollaboratorSummary[];
  commentCount?: number;
  attachmentCount?: number;
  tags?: TaskTag[];
  selected: boolean;
  selectionActive: boolean;
  onToggleSelect: (id: string) => void;
}

function DraggableTaskCard({
  task,
  onOpen,
  assignees,
  commentCount,
  attachmentCount,
  tags,
  selected,
  selectionActive,
  onToggleSelect,
}: DraggableTaskCardProps) {
  const { setNodeRef: setDragRef, listeners, attributes, isDragging, transform } =
    useDraggable({
      id: task.id,
      data: { task },
    });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `task:${task.id}`,
    data: { task },
  });

  return (
    <div
      ref={(node) => {
        setDragRef(node);
        setDropRef(node);
      }}
      style={{
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        opacity: isDragging ? 0.4 : 1,
      }}
      onDoubleClick={() => onOpen?.(task)}
      data-task-id={task.id}
      className={isOver ? 'ring-2 ring-cime-primary/40 rounded-lg' : undefined}
    >
      <div {...attributes} {...listeners}>
        <TaskCard
          task={task}
          variant="board"
          onOpen={onOpen}
          assignees={assignees}
          commentCount={commentCount}
          attachmentCount={attachmentCount}
          tags={tags}
          selectable
          selected={selected}
          selectionActive={selectionActive}
          onToggleSelect={onToggleSelect}
        />
      </div>
    </div>
  );
}
